import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { diffLines } from 'diff';
import { Maximize, Minimize } from 'lucide-react';
import Button from '../../components/UI/Button';
import './JsonCompare.css';

const JsonCompare = () => {
    const [json1, setJson1] = useState('');
    const [json2, setJson2] = useState('');
    const [diffResult, setDiffResult] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        compareJson();
    }, [json1, json2]);

    const formatJson = (input) => {
        if (!input.trim()) return '';
        try {
            const parsed = JSON.parse(input);
            return JSON.stringify(parsed, null, 2);
        } catch (e) {
            return input; // Return original if invalid, to show diff of text
        }
    };

    const compareJson = () => {
        const formatted1 = formatJson(json1);
        const formatted2 = formatJson(json2);

        const diff = diffLines(formatted1, formatted2);
        setDiffResult(diff);
    };

    const handleBeautify = () => {
        try {
            if (json1) setJson1(JSON.stringify(JSON.parse(json1), null, 2));
            if (json2) setJson2(JSON.stringify(JSON.parse(json2), null, 2));
            setError(null);
        } catch (err) {
            setError('Invalid JSON found. Cannot beautify.');
        }
    };

    return (
        <div className="tool-page container">
            <Helmet>
                <title>JSON Compare - Diff Two JSON Files | OnlineToolSphere</title>
                <meta name="description" content="Compare two JSON objects side-by-side and highlight differences. Semantic JSON comparison tool for developers." />
                <meta property="og:title" content="JSON Compare - Diff Two JSON Files | OnlineToolSphere" />
                <meta property="og:description" content="Compare two JSON objects side-by-side and highlight differences. Semantic JSON comparison tool for developers." />
                <meta property="og:url" content="https://onlinetoolsphere.com/tools/json-compare/" />
                <meta name="twitter:title" content="JSON Compare - Diff Two JSON Files | OnlineToolSphere" />
                <meta name="twitter:description" content="Compare two JSON objects side-by-side and highlight differences. Semantic JSON comparison tool for developers." />
            </Helmet>
            <div className="tool-header">
                <h1 className="text-gradient">JSON Compare</h1>
                <p className="tool-subtitle">Compare two JSON objects semantically.</p>
            </div>

            <div className="tool-actions">
                <Button onClick={handleBeautify}>
                    <Maximize size={16} style={{ marginRight: '0.5rem' }} /> Compare JSON
                </Button>
                {error && <span className="error-text">{error}</span>}
            </div>

            <div className="compare-grid">
                <div className="input-column">
                    <div className="pane-header">JSON A</div>
                    <textarea
                        className="compare-input"
                        value={json1}
                        onChange={(e) => setJson1(e.target.value)}
                        placeholder="Paste JSON to compare here..."
                        spellCheck="false"
                    />
                </div>
                <div className="input-column">
                    <div className="pane-header">JSON B</div>
                    <textarea
                        className="compare-input"
                        value={json2}
                        onChange={(e) => setJson2(e.target.value)}
                        placeholder="Paste JSON to be compared with here..."
                        spellCheck="false"
                    />
                </div>
            </div>

            <div className="diff-output">
                <div className="pane-header">Differences</div>
                <div className="diff-viewer">
                    {diffResult.map((part, index) => {
                        const color = part.added ? 'var(--diff-add-bg)' : part.removed ? 'var(--diff-rem-bg)' : 'transparent';
                        const textColor = part.added ? 'var(--diff-add-text)' : part.removed ? 'var(--diff-rem-text)' : 'var(--text-main)';
                        return (
                            <span key={index} style={{ backgroundColor: color, color: textColor }}>
                                {part.value}
                            </span>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default JsonCompare;
